import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    LineChart, Line, ReferenceLine,
} from 'recharts';
import './CountDisplayApp.css';
import { Sun, Moon, Users, Wifi, WifiOff, RefreshCw, Clock } from 'lucide-react';

const THEME_KEY = 'infrared_theme_preference';
const LANGUAGE_KEY = 'infrared_language_preference';

const translations = {
    en: {
        languageLabel: '🇬🇧',
        appTitle: 'People Counter',
        toggleDarkMode: 'Toggle dark mode',
        toggleLanguage: 'Switch language',
        current: 'Current',
        busy: 'BUSY',
        lowTraffic: 'LOW TRAFFIC',
        normalTraffic: 'NORMAL',
        noData: 'No data',
        busyMoreThanUsual: 'More active than usual',
        normalActivity: 'Normal activity',
        overview: 'Overview',
        refresh: 'Refresh',
        today: 'Today',
        month: 'Month',
        year: 'Year',
        total: 'Total',
        lastUpdated: 'Last updated',
        connection: 'Connection',
        online: 'Online',
        offline: 'Offline',
        lastSignal: 'Last signal',
        todayVsAvg: 'Today vs Avg (hourly)',
        avg28Days: 'Avg (28 days)',
        avgLegend: 'Avg (28 days)',
        avgHelper: 'Avg = per-hour average over the last 28 days',
        now: 'Now',
        dailyVisitors: 'Daily visitors',
        apply: 'Apply',
        start: 'Start',
        end: 'End',
        monthlyStats: 'Monthly statistics',
        errorPrefix: 'Error',
    },
    de: {
        languageLabel: '🇩🇪',
        appTitle: 'People Counter',
        toggleDarkMode: 'Dark Mode umschalten',
        toggleLanguage: 'Sprache wechseln',
        current: 'Aktuell',
        busy: 'BUSY',
        lowTraffic: 'WENIG BESUCHT',
        normalTraffic: 'NORMAL',
        noData: 'Keine Daten',
        busyMoreThanUsual: 'Mehr los als üblich',
        normalActivity: 'Normaler Betrieb',
        overview: 'Übersicht',
        refresh: 'Aktualisieren',
        today: 'Heute',
        month: 'Monat',
        year: 'Jahr',
        total: 'Gesamt',
        lastUpdated: 'Zuletzt aktualisiert',
        connection: 'Verbindung',
        online: 'Online',
        offline: 'Offline',
        lastSignal: 'Letztes Signal',
        todayVsAvg: 'Heute vs Ø (stündlich)',
        avg28Days: 'Ø (28 Tage)',
        avgLegend: 'Ø (28 Tage)',
        avgHelper: 'Ø = Durchschnitt pro Stunde über die letzten 28 Tage',
        now: 'Jetzt',
        dailyVisitors: 'Tägliche Besucher',
        apply: 'Anwenden',
        start: 'Start',
        end: 'Ende',
        monthlyStats: 'Monatliche Statistik',
        errorPrefix: 'Fehler',
    },
};

// Polling + request hardening
const POLL_MS = 120000; // 2 minutes
const FETCH_TIMEOUT_MS = 8000;

async function fetchWithTimeout(url, { timeoutMs = FETCH_TIMEOUT_MS, ...opts } = {}) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...opts, signal: controller.signal });
    } finally {
        clearTimeout(t);
    }
}

function isoDay(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}

function daysAgoIso(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

export default function CountDisplayApp() {
    const [counts, setCounts] = useState({ today: null, month: null, year: null, total: null });
    const [history, setHistory] = useState([]);
    const [todayHourly, setTodayHourly] = useState([]);
    const [avgHourly, setAvgHourly] = useState([]);
    const [daily, setDaily] = useState([]);
    const [busy, setBusy] = useState({ isBusy: null, lastHour: null, avgThisHour: null, ratio: null });
    const [nowTime, setNowTime] = useState(() => new Date());

    const lastReceivedRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const [darkMode, setDarkMode] = useState(() => localStorage.getItem(THEME_KEY) === 'dark');
    const [language, setLanguage] = useState(() => {
        const stored = localStorage.getItem(LANGUAGE_KEY);
        return stored && translations[stored] ? stored : 'de';
    });
    const t = useMemo(() => translations[language] ?? translations.de, [language]);
    const locale = language === 'de' ? 'de-DE' : 'en-US';

    // ✅ Single base URL
    const API_BASE = process.env.REACT_APP_API_BASE_URL;

    const configError = useMemo(() => {
        if (!API_BASE) return 'Missing REACT_APP_API_BASE_URL';
        return null;
    }, [API_BASE]);

    // Build endpoints from base
    const endpoints = useMemo(() => {
        if (!API_BASE) return null;
        const base = API_BASE.replace(/\/+$/, '');
        return {
            state: `${base}/api/state`,
            hourly: `${base}/api/hourly`,
            avgHourly: `${base}/api/avg-hourly`,
            daily: `${base}/api/daily`,
            busy: `${base}/api/busy`,
        };
    }, [API_BASE]);

    // Date range picker
    const [rangeStart, setRangeStart] = useState(() => daysAgoIso(27));
    const [rangeEnd, setRangeEnd] = useState(() => isoDay(Date.now()));

    useEffect(() => {
        document.documentElement.classList.toggle('dark', darkMode);
        try {
            localStorage.setItem(THEME_KEY, darkMode ? 'dark' : 'light');
        } catch {
            // ignore
        }
    }, [darkMode]);

    useEffect(() => {
        try {
            localStorage.setItem(LANGUAGE_KEY, language);
        } catch {
            // ignore
        }
    }, [language]);

    const fetchDaily = useCallback(async (startIso, endIso) => {
        if (!endpoints) return;
        const url = `${endpoints.daily}?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`;
        const res = await fetchWithTimeout(url, { timeoutMs: FETCH_TIMEOUT_MS });
        if (!res.ok) throw new Error(`Failed to fetch daily (${res.status})`);
        const json = await res.json();

        const rows = Array.isArray(json?.days) ? json.days : [];
        setDaily(
            rows.map((d) => ({
                date: d.date || '',
                count: typeof d.count === 'number' ? d.count : Number(d.count) || 0,
            }))
        );
    }, [endpoints]);

    const fetchData = useCallback(async () => {
        if (configError || !endpoints) {
            setError(configError || 'Missing endpoints');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const startedAt = new Date();
            const todayIso = isoDay(Date.now());

            const [stateRes, hourlyRes, avgRes, busyRes] = await Promise.all([
                fetchWithTimeout(endpoints.state, { timeoutMs: FETCH_TIMEOUT_MS }),
                fetchWithTimeout(`${endpoints.hourly}?date=${encodeURIComponent(todayIso)}`, { timeoutMs: FETCH_TIMEOUT_MS }),
                fetchWithTimeout(`${endpoints.avgHourly}?days=28`, { timeoutMs: FETCH_TIMEOUT_MS }),
                fetchWithTimeout(endpoints.busy, { timeoutMs: FETCH_TIMEOUT_MS }),
            ]);

            if (!stateRes.ok) throw new Error(`Failed to fetch state (${stateRes.status})`);
            if (!hourlyRes.ok) throw new Error(`Failed to fetch hourly (${hourlyRes.status})`);
            if (!avgRes.ok) throw new Error(`Failed to fetch avg-hourly (${avgRes.status})`);
            if (!busyRes.ok) throw new Error(`Failed to fetch busy (${busyRes.status})`);

            const state = await stateRes.json();
            const hourly = await hourlyRes.json();
            const avg = await avgRes.json();
            const busyJson = await busyRes.json();

            setCounts({
                total: typeof state.total === 'number' ? state.total : Number(state.total) || 0,
                today: typeof state.today === 'number' ? state.today : Number(state.today) || 0,
                month: typeof state.month === 'number' ? state.month : Number(state.month) || 0,
                year: typeof state.year === 'number' ? state.year : Number(state.year) || 0,
            });

            const hist = Array.isArray(state.history) ? state.history : [];
            setHistory(hist.map((e) => ({
                month: e.monthKey ?? e.month ?? '',
                count: typeof e.count === 'number' ? e.count : Number(e.count) || 0,
            })));

            const lr = typeof state.lastReceived === 'string' ? new Date(state.lastReceived) : null;
            if (lr && !Number.isNaN(lr.getTime())) lastReceivedRef.current = lr;

            // /api/hourly -> { date, hours:[{hour,value}] }
            const hours = Array.isArray(hourly?.hours) ? hourly.hours : [];
            const filledToday = Array.from({ length: 24 }, (_, h) => {
                const found = hours.find((x) => Number(x.hour) === h);
                return { hour: h, value: found ? (Number(found.value) || 0) : 0 };
            });
            setTodayHourly(filledToday);

            // /api/avg-hourly -> { days, hours:[{hour,value}] }
            const avgHours = Array.isArray(avg?.hours) ? avg.hours : (Array.isArray(avg?.points) ? avg.points : []);
            const filledAvg = Array.from({ length: 24 }, (_, h) => {
                const found = avgHours.find((x) => Number(x.hour) === h);
                const v = found ? (Number(found.value ?? found.avg) || 0) : 0;
                return { hour: h, value: Number.isFinite(v) ? Number(v.toFixed(2)) : 0 };
            });
            setAvgHourly(filledAvg);

            setBusy({
                isBusy: busyJson?.isBusy == null ? null : !!busyJson.isBusy,
                lastHour: busyJson?.lastHour != null ? Number(busyJson.lastHour) : null,
                avgThisHour: busyJson?.avgThisHour != null ? Number(busyJson.avgThisHour) : null,
                ratio: busyJson?.ratio != null ? Number(busyJson.ratio) : null,
            });

            setLastUpdated(startedAt);
            setError(null);
        } catch (err) {
            setError(err?.name === 'AbortError' ? 'Request timed out' : (err?.message || 'Unknown error'));
        } finally {
            window.setTimeout(() => setLoading(false), 250);
        }
    }, [configError, endpoints]);

    const applyRange = useCallback(async () => {
        try {
            setError(null);
            await fetchDaily(rangeStart, rangeEnd);
        } catch (err) {
            setError(err?.name === 'AbortError' ? 'Request timed out' : (err?.message || 'Unknown error'));
        }
    }, [fetchDaily, rangeStart, rangeEnd]);

    useEffect(() => {
        fetchData();
        fetchDaily(rangeStart, rangeEnd).catch(() => {});
        const iv = window.setInterval(fetchData, POLL_MS);
        return () => window.clearInterval(iv);
        // eslint-disable-next-line
    }, [fetchData, fetchDaily]);

    useEffect(() => {
        const iv = window.setInterval(() => setNowTime(new Date()), 60000);
        return () => window.clearInterval(iv);
    }, []);

    const formatTime = (date) =>
        date?.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    const now = Date.now();
    const last = lastReceivedRef.current;
    const diffSeconds = last ? (now - last.getTime()) / 1000 : Infinity;
    const isOnline = diffSeconds <= 3 * 60 * 60;
    const currentHour = useMemo(() => nowTime.getHours() + (nowTime.getMinutes() / 60), [nowTime]);
    const nowLabel = useMemo(() => (
        ({ viewBox }) => {
            if (!viewBox) return null;
            const { x = 0, y = 0 } = viewBox;
            const labelWidth = 22;
            const labelHeight = 18;
            const labelX = x - (labelWidth / 2);
            const labelY = y - (labelHeight + 6);
            return (
                <g transform={`translate(${labelX}, ${labelY})`}>
                    <rect
                        width={labelWidth}
                        height={labelHeight}
                        rx={9}
                        fill="var(--now-bg)"
                        stroke="var(--now)"
                        strokeWidth={1}
                    />
                    <g transform="translate(4, 2)">
                        <Clock size={14} color="var(--now)" strokeWidth={2} />
                    </g>
                </g>
            );
        }
    ), []);

    const hourTicks = useMemo(() => Array.from({ length: 24 }, (_, h) => h), []);
    const overlay = useMemo(() => (
        Array.from({ length: 24 }, (_, h) => ({
            hour: h,
            today: todayHourly[h]?.value ?? 0,
            avg: avgHourly[h]?.value ?? 0,
        }))
    ), [todayHourly, avgHourly]);

    const busyState = useMemo(() => {
        if (busy.ratio != null && Number.isFinite(busy.ratio)) {
            if (busy.ratio >= 1.2) return 'busy';
            if (busy.ratio <= 0.8) return 'low';
            return 'normal';
        }
        if (busy.isBusy === true) return 'busy';
        if (busy.isBusy === false) return 'normal';
        return 'unknown';
    }, [busy.isBusy, busy.ratio]);

    const busyText = useMemo(() => {
        if (busyState === 'unknown') return '—';
        if (busyState === 'busy') return t.busy;
        if (busyState === 'low') return t.lowTraffic;
        return t.normalTraffic;
    }, [busyState, t]);

    const busySub = useMemo(() => {
        if (busyState === 'unknown') return t.noData;
        if (busyState === 'busy') return t.busyMoreThanUsual;
        return t.normalActivity;
    }, [busyState, t]);

    return (
        <div className="app">
            <header className="app-header">
                <h1>
                    <Users size={28} />
                    {t.appTitle}
                </h1>
                <div className="header-actions">
                    <button
                        className="lang-toggle"
                        onClick={() => setLanguage((prev) => (prev === 'de' ? 'en' : 'de'))}
                        aria-label={t.toggleLanguage}
                    >
                        {t.languageLabel}
                    </button>
                    <button
                        className="theme-toggle"
                        onClick={() => setDarkMode(!darkMode)}
                        aria-label={t.toggleDarkMode}
                    >
                        {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                    </button>
                </div>
            </header>

            <main className="app-main">
                <div className="layout-grid">

                    {/* Busy */}
                    <div className="card busy-card">
                        <h2>{t.current}</h2>
                        <div className={`busy-pill ${busyState}`}>
                            {busyText}
                        </div>
                        <p className="busy-sub">{busySub}</p>
                    </div>

                    {/* Overview */}
                    <div className="card overview-card">
                        <div className="card-head">
                            <h2>{t.overview}</h2>
                            <button
                                onClick={fetchData}
                                disabled={loading || !!configError}
                                className="btn icon-btn primary"
                                title={t.refresh}
                                aria-label={t.refresh}
                            >
                                {loading ? <RefreshCw className="spin" size={18} /> : <RefreshCw size={18} />}
                            </button>
                        </div>

                        {(error || configError) && (
                            <p className="error-text">
                                {t.errorPrefix}: {error || configError}
                            </p>
                        )}

                        <div className="count-grid">
                            {[
                                { label: t.today, value: counts.today },
                                { label: t.month, value: counts.month },
                                { label: t.year, value: counts.year },
                                { label: t.total, value: counts.total },
                            ].map(({ label, value }) => (
                                <div className="count-box" key={label}>
                                    <p className="count-label">{label}</p>
                                    <p className="count-value">{value != null ? value : '-'}</p>
                                </div>
                            ))}
                        </div>

                        <p className="updated-text">
                            {t.lastUpdated}: {lastUpdated ? formatTime(lastUpdated) : 'N/A'}
                        </p>
                    </div>

                    {/* Connection */}
                    <div className="card status-card">
                        <h2>{t.connection}</h2>
                        {lastUpdated ? (
                            <div className="status-content">
                                <div className={`status-icon ${isOnline ? 'online' : 'offline'}`}>
                                    {isOnline ? <Wifi size={40} /> : <WifiOff size={40} />}
                                </div>
                                <p className={`status-text ${isOnline ? 'online' : 'offline'}`}>
                                    {isOnline ? t.online : t.offline}
                                </p>
                                <p className="last-seen-text">
                                    {t.lastSignal}: {last ? last.toLocaleTimeString(locale) : 'N/A'}
                                </p>
                            </div>
                        ) : (
                            <p className="status-text offline">{t.noData}</p>
                        )}
                    </div>

                    {/* Chart: Today vs Avg */}
                    <div className="card chart-card today-chart">
                        <h2>{t.todayVsAvg}</h2>
                        <div className="legend-row">
                            <span><span className="dot today" /> {t.today}</span>
                            <span><span className="dot avg" /> {t.avgLegend}</span>
                        </div>

                        <div className="chart-wrap">
                            <ResponsiveContainer width="100%" height={280}>
                                <LineChart data={overlay} margin={{ top: 28, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="hour"
                                        type="number"
                                        domain={[0, 23]}
                                        ticks={hourTicks}
                                        allowDecimals={false}
                                        tickFormatter={(h) => `${String(h).padStart(2, '0')}:00`}
                                    />
                                    <YAxis />
                                    <Tooltip
                                        labelFormatter={(h) => `${String(h).padStart(2, '0')}:00`}
                                        formatter={(v, name) => [v, name === 'today' ? t.today : t.avgLegend]}
                                    />
                                    <ReferenceLine
                                        x={currentHour}
                                        stroke="var(--now)"
                                        strokeDasharray="4 4"pm
                                        ifOverflow="visible"
                                        label={nowLabel}
                                    />
                                    {/* ✅ Avg clearly different */}
                                    <Line type="monotone" dataKey="avg" dot={false} strokeWidth={3} stroke="var(--avg)" />
                                    <Line type="monotone" dataKey="today" dot={false} strokeWidth={3} stroke="var(--value)" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <p className="muted">{t.avgHelper}</p>
                    </div>

                    {/* Chart: Daily range */}
                    <div className="card chart-card daily-chart">
                        <div className="card-head">
                            <h2>{t.dailyVisitors}</h2>
                            <button className="btn primary" onClick={applyRange} disabled={!!configError}>
                                {t.apply}
                            </button>
                        </div>

                        <div className="range-controls">
                            <div className="range-field">
                                <label>{t.start}</label>
                                <input className="input" type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
                            </div>
                            <div className="range-field">
                                <label>{t.end}</label>
                                <input className="input" type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
                            </div>
                        </div>

                        <div className="chart-wrap">
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={daily} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="var(--value)" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Chart: Monthly */}
                    <div className="card chart-card monthly-chart">
                        <h2>{t.monthlyStats}</h2>
                        <div className="chart-wrap">
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={history} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="var(--value)" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                </div>
            </main>

            <footer className="app-footer">
                API base: <code>{API_BASE || '-'}</code>
            </footer>
        </div>
    );
}
